import React from "react"
import { Link, graphql, useStaticQuery, navigate } from "gatsby"
import {
  EListingCardLayouts,
  ListingCard,
} from "@organic-return/foundation-react/src/components/Listing/ListingCard"
import { EListingVitalsLayout } from "@organic-return/foundation-react/src/components/Listing/ListingVitals"
import { Search } from "@organic-return/foundation-react/src/components/Search/Search"
import { SiteContext } from "../common/SiteContext"

const ListingSearch = ({ module, page }) => {
  const data = useStaticQuery(graphql`
    query ListingSearchQuery {
      allListing(sort: {order: DESC, fields: price}) {
        nodes {
          id
          baths
          bathsHalf
          baths_3_4
          address {
            city
            street1
            county
            postal
            text
            street2
            state
          }
          beds
          lotacres
          majorArea
          minorArea
          mlsID
          price
          sqft
          status
          statusTS
          subType
          subdivision
          neighborhood
          exclusive
          features
          flags
          type
          defaultImage {
            dimensions
            url
          }
          path
          location {
            lat
            lng
          }
          hasWaterfront
          hasWaterViews
          hasBeachfront
          hasGolfCourseView
          hasOpenHouse
          hideOnMap
          propertyStyles
          garageSpaces
          hasVirtualTour
          vacationRentable
          amenities
          lifestyles
          rooms
          parkingFeatures
          newlyBuilt
          onMktTS
          officeName
          agent {
            mlsID
          }
          office {
            mlsID
          }
          isDistinctive
        }
      }      
      allRental(sort: {order: DESC, fields: price}) {
        nodes {
          baths
          bathsHalf
          address {
            city
            state
            text
            street1
            street2
            postal
          }
          features
          beds
          sleeps
          type
          path
          price
          priceLow
          priceHigh
          priceSuffix
          bookings {
            endDate
            startDate
          }
          rates {
            price
            startDate
            endDate
            type
          }
          defaultImage {
            dimensions
            url
            caption
          }
          location {
            lat
            lng
          }
          seasonality
          agent {
            mlsID
          }
          office {
            mlsID
          }
          exclusive
        }
      }
      allAgilityListingSearch {
        nodes {
          contentID
          filter {
            customFields {
              query
              exclusive
              mLSIDs
              majorAreas
              minorAreas
              cities
              postalCodes
              types
              subTypes
              counties
              subdivisions
              neighborhoods
              statuses
              officeMLSIDs
              agentMLSIDs
              features
              openHouse
              hasVirtualTour
              flags
            }
          }
        }
      }
    }
  `)

  let initialFilter = data.allAgilityListingSearch.nodes.find((node: any) => {
    return node.contentID == module.contentID
  }).filter[0]?.customFields.query

  if (initialFilter) {
    initialFilter = initialFilter.replace(/\+/g, '%20')
    initialFilter = decodeURIComponent(initialFilter)
  }

  /**
   * Had to remove the status filter (!closed) from the static query
   * because we found a situation where closed listings were needed in
   * the results. Now we can control if closed listings should be displayed
   * in the module.
   */
  let listings: any[]
  if (module.customFields?.showClosedListings == "true") {
    listings = data.allListing.nodes
  }
  else {
    listings = data.allListing.nodes.filter((listing) => {
      return listing.status != "Closed"
    })
  }

  if (process.env.HIDE_RENTALS_FROM_LISTINGS_SEARCH === "0" || module.customFields.addRentalsToResults === "true") {
    listings.push(...data.allRental.nodes);
  }

  let agilitySearchFilter = data.allAgilityListingSearch.nodes.find((node: any) => {
    return node.contentID == module.contentID
  }).filter[0];

  if (agilitySearchFilter) {
    const {
      exclusive,
      mLSIDs,
      majorAreas,
      minorAreas,
      cities,
      postalCodes,
      types,
      subTypes,
      subdivisions,
      neighborhoods,
      counties,
      statuses,
      officeMLSIDs,
      agentMLSIDs,
      features,
      openHouse,
      hasVirtualTour,
      flags
    } = agilitySearchFilter.customFields;


    if (exclusive && exclusive == "true") {
      listings = listings.filter(value => value.exclusive);
    }
    if (mLSIDs && mLSIDs.length > 0) {
      const mlsidArray = mLSIDs.split(",").map(str => str.trim());
      listings = listings.filter(value => mlsidArray.includes(value.mlsID))
    }
    if (majorAreas && majorAreas.length > 0) {
      const majorAreasArray = majorAreas.split(",").map(str => str.trim());
      listings = listings.filter(value => majorAreasArray.includes(value.majorArea))
    }
    if (minorAreas && minorAreas.length > 0) {
      const minorAreasArray = minorAreas.split(",").map(str => str.trim());
      listings = listings.filter(value => minorAreasArray.includes(value.minorArea))
    }
    if (cities && cities.length > 0) {
      const citiesArray = cities.split(",").map(str => str.trim());
      listings = listings.filter(value => value.address && citiesArray.includes(value.address.city))
    }
    if (postalCodes && postalCodes.length > 0) {
      const postalCodesArray = postalCodes.split(",").map(str => str.trim())
      listings = listings.filter(value => value.address && postalCodesArray.includes(value.address.postal))
    }
    if (types && types.length > 0) {
      const typesArray = types.split(",").map(str => str.trim());
      listings = listings.filter(value => typesArray.includes(value.type))
    }
    if (subTypes && subTypes.length > 0) {
      const subTypesArray = subTypes.split(",").map(str => str.trim());
      listings = listings.filter(value => subTypesArray.includes(value.subType))
    }
    if (statuses && statuses.length > 0) {
      const statusesArray = statuses.split(",").map(str => str.trim());
      listings = listings.filter(value => statusesArray.includes(value.status))
    }
    if (counties && counties.length > 0) {
      const countiesArray = counties.split(",").map(str => str.trim());
      listings = listings.filter(value => value.address && countiesArray.includes(value.address.county))
    }
    if (subdivisions && subdivisions.length > 0) {
      const subdivisionsArray = subdivisions.split(",").map(str => str.trim());
      listings = listings.filter(value => subdivisionsArray.includes(value.subdivision))
    }
    if (neighborhoods && neighborhoods.length > 0) {
      const neighborhoodsArray = neighborhoods.split(",").map(str => str.trim());
      listings = listings.filter(value => neighborhoodsArray.includes(value.neighborhood))
    }
    if (officeMLSIDs && officeMLSIDs.length > 0) {
      const officeMLSIDsArray = officeMLSIDs.split(",").map(str => str.trim());
      listings = listings.filter(value => value.office[0] && officeMLSIDsArray.includes(value.office[0].mlsID))
    }

    if (agentMLSIDs && agentMLSIDs.length > 0) {
      const agentMLSIDsArray = agentMLSIDs.split(",").map(str => str.trim());
      listings = listings.filter(value => value.agent[0] && agentMLSIDsArray.includes(value.agent[0].mlsID))
    }
    if (features && features.length > 0) {
      const featuresArray = features.split(",").map(str => str.trim());
      listings = listings.filter(value => featuresArray.includes(value.features))
    }
    if (openHouse && openHouse == "true") {
      listings = listings.filter(value => value.hasOpenHouse);
    }
    if (hasVirtualTour && hasVirtualTour == "true") {
      listings = listings.filter(value => value.hasVirtualTour);
    }
    if (flags && flags.length > 0) {
      const flagsArray = flags.split(",").map(str => str.trim());
      listings = listings.filter(value => flagsArray.includes(value.flags))
    }
  }

  // Small card size option (default)
  let cardClass = "w-[480px]"
  let cardImageWidth = 480
  // Large card size option
  if (module.customFields?.cardSize == "large") {
    cardClass = "w-[640px]"
    cardImageWidth = 640
  }

  // Configurable results per page defaults to 20
  let pageSize = module.customFields?.pageSize || 20
  return (
    <SiteContext.Consumer>{
      value => {
        return (
          <Search
            index={listings}
            initialFilter={initialFilter}
            pageSize={pageSize}
            pageSizeOpts={[pageSize * 1, pageSize * 2, pageSize * 3, pageSize * 4]}
            pagerBottom={true}
            showCount={true}
            useMap={true}
            mapClick={item => navigate(item.path)}
            renderItem={(item, index) => {
              return (
                <ListingCard
                  key={index}
                  listing={item}
                  layout={EListingCardLayouts.vertical}
                  className={`${cardClass} max-w-full`}
                  maxWidthImage={cardImageWidth}
                  linkImage={true}
                  linkFunc={props => {
                    return (
                      <Link
                        to={props.href ?? ""}
                        className={`text-inherit ${props.className}`}
                        key={props.key}
                        title={props.title}
                      >
                        {props.children}
                      </Link>
                    )
                  }}
                  {...value.globalListingCardProps}
                  vitalsLayout={
                    module.customFields?.cardLayout ||
                    EListingVitalsLayout.column2Border1
                  }
                />
              )
            }}
            formBackgroundImage={module.customFields.formBackgroundImage?.url}
            title={module.customFields.formTitle}
            preRenderItem={(item, index) => {
              return (
                <a key={index} href={item.path}></a>
              )
            }}
            {...value.listingSearchProps}
          />
        )
      }}
    </SiteContext.Consumer>
  )
}

export default ListingSearch
